/**
 * Tax withheld from suppliers, and paying it over.
 *
 * The reasoning column is the point. "Why was this vendor deducted at 20%" is a
 * question somebody asks six months later, and the answer is stored with the
 * deduction rather than recomputed — next year's rates would give a different
 * and wrong answer to the same question.
 *
 * Nothing here pays the government. Recording a deposit says a person made the
 * payment and entered the challan number that came back.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Field, Loading, Note, Pill } from '../components/ui'

interface Deduction {
  id: string
  party_id: string
  party_name: string
  invoice_reference: string | null
  deducted_on: string
  tds_head: string
  base_paise: number
  rate_bp: number
  tds_paise: number
  reason: string
  deposited: boolean
}

interface Position {
  period_month: string
  due_on: string
  deductions: number
  tds_paise: number
  deposited: boolean
  days_until_due: number
  interest_if_paid_today_paise: number
}

interface Challan {
  id: string
  period_month: string
  tds_head: string | null
  status: string
  due_on: string
  deposited_on: string | null
  challan_no: string | null
  tds_paise: number
  interest_paise: number
  total_paise: number
  is_late: boolean
  journal_entry_id: string | null
}

interface Head {
  name: string
  rate_bp: number
  alt_rate_bp: number | null
  threshold_single_paise: number | null
  threshold_aggregate_paise: number | null
  citation: string | null
}

interface Rulepack {
  fy_code: string
  available: string[]
  no_pan_rate_bp: number
  deposit_day: number
  heads: Head[]
}

interface Missed {
  invoice_id: string
  reference: string
  party_name: string
  would_deduct_paise: number
  reason: string
}

interface BankAccount {
  id: string
  name: string
  kind: string
}

const rate = (bp: number) => `${(bp / 100).toFixed(bp % 100 ? 2 : 0)}%`
const today = () => new Date().toISOString().slice(0, 10)

export default function Tds() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [showRules, setShowRules] = useState(false)
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['tds-position'] })
    void queryClient.invalidateQueries({ queryKey: ['tds-challans'] })
    void queryClient.invalidateQueries({ queryKey: ['tds-deductions'] })
  }

  const deductions = useQuery({
    queryKey: ['tds-deductions'],
    queryFn: () => api.get<Deduction[]>('/tax/tds/deductions'),
  })
  const position = useQuery({
    queryKey: ['tds-position'],
    queryFn: () => api.get<Position[]>('/tax/tds/position'),
  })
  const challans = useQuery({
    queryKey: ['tds-challans'],
    queryFn: () => api.get<Challan[]>('/tax/tds/challans'),
  })
  const missed = useQuery({
    queryKey: ['tds-missed'],
    queryFn: () => api.get<Missed[]>('/tax/tds/missed'),
  })
  const rulepack = useQuery({
    queryKey: ['tds-rulepack'],
    queryFn: () => api.get<Rulepack>('/tax/rulepack'),
    enabled: showRules,
  })
  const banks = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => api.get<BankAccount[]>('/banking/accounts'),
    enabled: can('org.bank_account.view'),
  })

  const prepare = useMutation({
    mutationFn: (month: string) =>
      api.post<Challan>('/tax/tds/challans', { period_month: month }),
    onSuccess: refresh,
  })
  const deposit = useMutation({
    mutationFn: ({ id, bank, on, no }: { id: string; bank: string; on: string; no: string }) =>
      api.post<Challan>(`/tax/tds/challans/${id}/deposit`, {
        bank_account_id: bank,
        deposited_on: on,
        challan_no: no || null,
      }),
    onSuccess: refresh,
  })

  if (deductions.isLoading) return <Loading what="Loading tax withheld" />
  const payFrom = (banks.data ?? []).filter((b) => b.kind !== 'cash')
  const open = position.data ?? []

  return (
    <>
      <ErrorNote
        error={deductions.error ?? prepare.error ?? deposit.error ?? missed.error}
      />

      {(missed.data ?? []).length > 0 && (
        <Note kind="error">
          <strong>
            {missed.data!.length === 1
              ? 'One posted bill should have been deducted from and was not.'
              : `${missed.data!.length} posted bills should have been deducted from and were not.`}
          </strong>{' '}
          That is money owed to the government that nothing else will tell you about.
          <ul>
            {missed.data!.map((m) => (
              <li key={m.invoice_id}>
                {m.reference} to {m.party_name} — {formatInr(m.would_deduct_paise)}
              </li>
            ))}
          </ul>
        </Note>
      )}

      {open.length > 0 && (
        <Card title="Owed to the government" hint="withheld and not yet paid over" tight>
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th className="num">Deductions</th>
                <th className="num">Withheld</th>
                <th>Due</th>
                <th className="num">If paid today</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {open.map((row) => (
                <tr key={row.period_month}>
                  <td>{row.period_month.slice(0, 7)}</td>
                  <td className="num">{row.deductions}</td>
                  <td className="num">{formatInr(row.tds_paise)}</td>
                  <td className="muted">
                    {row.due_on}
                    {row.days_until_due < 0 && (
                      <div className="faint">{-row.days_until_due} days ago</div>
                    )}
                  </td>
                  <td className="num">
                    {row.interest_if_paid_today_paise ? (
                      <>
                        {formatInr(row.interest_if_paid_today_paise)}
                        <div className="faint">interest</div>
                      </>
                    ) : (
                      <span className="faint">no interest</span>
                    )}
                  </td>
                  <td className="num">
                    {can('tax.challan.create') && (
                      <button
                        className="small primary"
                        disabled={prepare.isPending}
                        onClick={() => prepare.mutate(row.period_month)}
                      >
                        Prepare challan
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card title="Challans" hint={`${(challans.data ?? []).length}`} tight>
        {(challans.data ?? []).length === 0 ? (
          <div className="empty">Nothing prepared yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th>Due</th>
                <th className="num">Tax</th>
                <th className="num">Interest</th>
                <th className="num">Total</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(challans.data ?? []).map((challan) => (
                <tr key={challan.id}>
                  <td>{challan.period_month.slice(0, 7)}</td>
                  <td className="muted date">{challan.due_on}</td>
                  <td className="num">{formatInr(challan.tds_paise)}</td>
                  <td className="num">
                    {challan.interest_paise ? (
                      formatInr(challan.interest_paise)
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td className="num">{formatInr(challan.total_paise)}</td>
                  <td>
                    <Pill value={challan.status} />
                    {challan.is_late && <div className="faint">paid late</div>}
                    {challan.challan_no && (
                      <div className="faint mono">{challan.challan_no}</div>
                    )}
                  </td>
                  <td className="num">
                    {challan.status === 'draft' &&
                      can('tax.tds.approve') &&
                      payFrom.length > 0 && (
                        <DepositForm
                          challan={challan}
                          accounts={payFrom}
                          pending={deposit.isPending}
                          onDeposit={(bank, on, no) =>
                            deposit.mutate({ id: challan.id, bank, on, no })
                          }
                        />
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Note kind="info">
          Recording a deposit does not pay anything. Somebody pays on the portal and enters
          the challan number that comes back. Interest is worked out from the two dates
          rather than typed in.
        </Note>
      </Card>

      <Card
        title="What was withheld"
        hint={`${(deductions.data ?? []).length}`}
        tight
        actions={
          <button className="small" onClick={() => setShowRules(!showRules)}>
            {showRules ? 'Hide' : 'Show'} the rates
          </button>
        }
      >
        {(deductions.data ?? []).length === 0 ? (
          <div className="empty">Nothing withheld yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Vendor</th>
                <th>Bill</th>
                <th>Head</th>
                <th className="num">On</th>
                <th className="num">Rate</th>
                <th className="num">Withheld</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {(deductions.data ?? []).map((row) => (
                <tr key={row.id}>
                  <td className="muted date">{row.deducted_on}</td>
                  <td>{row.party_name}</td>
                  <td className="mono">
                    {row.invoice_reference ?? <span className="faint">—</span>}
                  </td>
                  <td className="muted">{row.tds_head.replace(/_/g, ' ')}</td>
                  <td className="num muted">{formatInr(row.base_paise)}</td>
                  <td className="num">{rate(row.rate_bp)}</td>
                  <td className="num">{formatInr(row.tds_paise)}</td>
                  <td className="faint">{row.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {showRules && rulepack.data && (
        <Card
          title={`The rates in force — FY ${rulepack.data.fy_code}`}
          hint="a YAML file with citations, edited on Budget day"
          tight
        >
          <table>
            <thead>
              <tr>
                <th>Head</th>
                <th className="num">Rate</th>
                <th className="num">Alternate</th>
                <th className="num">Single limit</th>
                <th className="num">Yearly limit</th>
                <th>Citation</th>
              </tr>
            </thead>
            <tbody>
              {rulepack.data.heads.map((head) => (
                <tr key={head.name}>
                  <td>{head.name.replace(/_/g, ' ')}</td>
                  <td className="num">{rate(head.rate_bp)}</td>
                  <td className="num">
                    {head.alt_rate_bp !== null ? (
                      rate(head.alt_rate_bp)
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td className="num muted">
                    {head.threshold_single_paise !== null ? (
                      formatInr(head.threshold_single_paise)
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td className="num muted">
                    {head.threshold_aggregate_paise !== null ? (
                      formatInr(head.threshold_aggregate_paise)
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td className="faint">{head.citation ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Note kind="info">
            No PAN, or a PAN that is not Aadhaar-linked, is{' '}
            {rate(rulepack.data.no_pan_rate_bp)} whatever the head says. Deposits are due
            on the {rulepack.data.deposit_day}th of the following month, except March,
            which has until 30 April.
          </Note>
        </Card>
      )}
    </>
  )
}

function DepositForm({
  challan,
  accounts,
  pending,
  onDeposit,
}: {
  challan: Challan
  accounts: BankAccount[]
  pending: boolean
  onDeposit: (bank: string, on: string, no: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [bank, setBank] = useState(accounts[0]?.id ?? '')
  const [on, setOn] = useState(today())
  const [no, setNo] = useState('')

  if (!open) {
    return (
      <button className="small primary" onClick={() => setOpen(true)}>
        Record deposit
      </button>
    )
  }

  return (
    <div className="row">
      <Field label="From">
        <select value={bank} onChange={(e) => setBank(e.target.value)}>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Paid on" caption={`due ${challan.due_on}`}>
        <input type="date" value={on} onChange={(e) => setOn(e.target.value)} />
      </Field>
      <Field label="Challan number">
        <input value={no} onChange={(e) => setNo(e.target.value)} />
      </Field>
      <div className="shrink">
        <button
          className="small primary"
          disabled={pending}
          onClick={() => onDeposit(bank, on, no)}
        >
          Save
        </button>
      </div>
    </div>
  )
}
